/**
 * ActivationResultContract (AJIP-16.02.006 · WS11)
 * ---------------------------------------------------------------------
 * Every activation attempt returns an Activation Result. Immutable.
 * Auditable.
 *
 * Founder Ruling A2:
 *   The Activation Result shall expose NO Core Engine object. Callers
 *   holding a journeyInstanceId may look up the context via
 *   getJourneyEngine().contextOf(id) through the published interface.
 */

const ACTIVATION_STATUSES = Object.freeze({
  ACTIVATED: 'ACTIVATED',
  ALREADY_ACTIVE: 'ALREADY_ACTIVE',
  RESTORED: 'RESTORED',
  RESUMED: 'RESUMED',
  REJECTED: 'REJECTED',
  BLOCKED: 'BLOCKED',
  FAILED: 'FAILED',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  CONFLICT: 'CONFLICT',
});

const IDEMPOTENCY_STATUSES = Object.freeze({
  FIRST: 'FIRST',
  REPLAYED: 'REPLAYED',
});

const RESTORATION_STATUSES = Object.freeze({
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  RESTORED_FROM_PERSISTENCE: 'RESTORED_FROM_PERSISTENCE',
  RESUMED_FROM_MEMORY: 'RESUMED_FROM_MEMORY',
  REJECTED: 'REJECTED',
});

let _seq = 0;
const _now = () => Date.now();
const _mintResultId = (nowFn) => {
  _seq = (_seq + 1) & 0xffff;
  return `act_${(nowFn ? nowFn() : _now()).toString(36)}_${_seq.toString(36)}`;
};

const makeActivationResult = ({
  activationResultId,
  requestId,
  resolutionDecisionId,
  journeyInstanceId = null,
  journeyId,
  journeyVersion = null,
  status,
  initialState = null,
  principalRef = null,
  idempotencyStatus = IDEMPOTENCY_STATUSES.FIRST,
  conflictStatus = 'none',
  restorationStatus = RESTORATION_STATUSES.NOT_APPLICABLE,
  reason = 'OK',
  at,
  auditMetadata = {},
  nowFn = _now,
}) => {
  if (typeof status !== 'string' || !Object.values(ACTIVATION_STATUSES).includes(status)) {
    throw new Error(`makeActivationResult: unknown status '${status}'`);
  }
  const id = activationResultId || _mintResultId(nowFn);
  const timestamp = at || new Date(nowFn()).toISOString();
  return Object.freeze({
    activationResultId: id,
    requestId: requestId || null,
    resolutionDecisionId: resolutionDecisionId || null,
    journeyInstanceId,
    journeyId: journeyId || null,
    journeyVersion,
    status,
    initialState,
    principalRef,
    idempotencyStatus,
    conflictStatus,
    restorationStatus,
    reason,
    at: timestamp,
    auditMetadata: Object.freeze({ ...auditMetadata }),
  });
};

const buildJourneyInstanceId = (principalRef, journeyId) => {
  const owner = principalRef == null ? 'anon' : String(principalRef);
  return `${owner}::${journeyId}`;
};

export {
  ACTIVATION_STATUSES,
  IDEMPOTENCY_STATUSES,
  RESTORATION_STATUSES,
  makeActivationResult,
  buildJourneyInstanceId,
};
